import { useState } from 'react';
import { Link } from 'react-router-dom';
import { algebraQuestions } from './data/algebraQuestions';
import QuestionCard from './components/QuestionCard';
import ProgressBar from './components/ProgressBar';
import ResultsSummary from './components/ResultsSummary';
import { useScoreHistory } from '../../../hooks/useScoreHistory';

export default function AlgebraPracticePage() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<(string | null)[]>(
    new Array(algebraQuestions.length).fill(null)
  );
  const [showResults, setShowResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const { saveScore } = useScoreHistory();

  const currentQuestion = algebraQuestions[currentQuestionIndex];
  const answeredCount = userAnswers.filter((answer) => answer !== null).length;
  const isLastQuestion = currentQuestionIndex === algebraQuestions.length - 1;
  const canGoNext = userAnswers[currentQuestionIndex] !== null;

  const handleAnswerSelect = (answer: string) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answer;
    setUserAnswers(newAnswers);
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calculate and save score
      const correctCount = userAnswers.filter(
        (ans, idx) => ans === algebraQuestions[idx].correctAnswer
      ).length;
      const percentage = Math.round((correctCount / algebraQuestions.length) * 100);
      
      saveScore({
        examName: 'SAT Math: Algebra Practice',
        domain: 'Algebra',
        examType: 'math',
        score: correctCount,
        total: algebraQuestions.length,
        percentage,
      });
      
      setShowResults(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleReviewAnswers = () => {
    setReviewMode(true);
    setShowResults(false);
    setCurrentQuestionIndex(0);
  };

  const handleRetakeTest = () => {
    setUserAnswers(new Array(algebraQuestions.length).fill(null));
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setReviewMode(false);
  };

  const handleExitReview = () => {
    setShowResults(true);
  };

  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-12 px-4">
        <div className="max-w-4xl mx-auto mb-6">
          <Link
            to="/sat-prep"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to SAT Prep
          </Link>
        </div>
        <ResultsSummary
          questions={algebraQuestions}
          userAnswers={userAnswers}
          onReviewAnswers={handleReviewAnswers}
          onRetakeTest={handleRetakeTest}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-12 px-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <Link
          to="/sat-prep"
          className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold mb-6 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          Back to SAT Prep
        </Link>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            SAT Math: Algebra Practice Test
          </h1>
          <p className="text-gray-600">
            20 questions covering linear equations, functions, inequalities, and systems
          </p>
          {reviewMode && (
            <div className="mt-4 flex items-center gap-3">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                Review Mode
              </span>
              <button
                onClick={handleExitReview}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Exit Review
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {!reviewMode && (
        <ProgressBar
          current={currentQuestionIndex + 1}
          total={algebraQuestions.length}
          answered={answeredCount}
        />
      )}

      {/* Question Card */}
      <QuestionCard
        question={currentQuestion}
        questionNumber={currentQuestionIndex + 1}
        totalQuestions={algebraQuestions.length}
        selectedAnswer={userAnswers[currentQuestionIndex]}
        onAnswerSelect={handleAnswerSelect}
        showSolution={reviewMode || userAnswers[currentQuestionIndex] !== null}
      />

      {/* Navigation */}
      <div className="max-w-4xl mx-auto mt-6 flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Previous
        </button>

        {reviewMode ? (
          <div className="flex gap-3">
            {currentQuestionIndex < algebraQuestions.length - 1 && (
              <button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                Next Question
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
            {currentQuestionIndex === algebraQuestions.length - 1 && (
              <button
                onClick={handleExitReview}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                Back to Results
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            {isLastQuestion ? 'Submit Test' : 'Next'}
            <i className={isLastQuestion ? 'ri-check-line' : 'ri-arrow-right-line'}></i>
          </button>
        )}
      </div>

      {/* Question Navigator (Review Mode) */}
      {reviewMode && (
        <div className="max-w-4xl mx-auto mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Jump to Question:</h3>
          <div className="grid grid-cols-10 gap-2">
            {algebraQuestions.map((_, index) => {
              const isCorrect = userAnswers[index] === algebraQuestions[index].correctAnswer;
              return (
                <button
                  key={index}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all ${
                    currentQuestionIndex === index
                      ? 'bg-emerald-600 text-white ring-2 ring-emerald-300'
                      : isCorrect
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}